/**
 * Release sections data (dashboard rows, nav badges, calendar nudge, Brand Portfolio inbox).
 *
 * - Brand scope hooks use the effective team (God Mode team included) and only run where the
 *   release workflow can exist: a brand context (not agency staff outside God Mode) of an agency
 *   brand with carte blanche OFF. The server answers `workflowActive: false` otherwise.
 * - No auto retry loops beyond the app default; failures surface a manual retry in the UI.
 * - Marking a surface as seen never invalidates the sections (it would wipe the "new" cues);
 *   the nav badge cache is patched directly instead.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { releaseSectionsService } from '@/services/releaseSections.service';
import { releaseInboxKeys, teamQueryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/hooks/useAuth';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import {
  releaseItemKey,
  type BrandReleaseSections,
  type NewlyReleased,
  type ReleaseBadges,
  type ReleaseInboxCounts,
  type ReleaseInboxSectionKey,
  type ReleaseSurface,
  type SectionItem,
} from '@/types/releaseSections';

export const RELEASE_BADGES_POLL_MS = 60_000;
const INBOX_COUNTS_POLL_MS = 120_000;
export const RELEASE_INBOX_PAGE_SIZE = 20;
export const DASHBOARD_SECTION_LIMIT = 10;

/** Queries are `enabled` only with a team; this satisfies the type checker without `!`. */
function requireTeamId(teamId: number | null): number {
  if (teamId === null) throw new Error('No team selected');
  return teamId;
}

export interface ReleaseBrandGate {
  teamId: number | null;
  /** True when the viewer is inside a brand (agency staff only via God Mode) whose release workflow can be on */
  enabled: boolean;
}

/** Where brand-scope release data may be fetched at all. */
export function useReleaseBrandGate(): ReleaseBrandGate {
  const { teamId, hasTeam, isAgencyStaff, isInGodMode } = useEffectiveTeamId();
  const { isAgencyTeam, carteBlanche } = useReviewerRestrictions();
  const inBrandContext = hasTeam && (!isAgencyStaff || isInGodMode);
  return { teamId, enabled: inBrandContext && isAgencyTeam && !carteBlanche };
}

// ============================================================================
// Nav badges
// ============================================================================

/** Counts for the nav (approvals = waiting for your release, calendar = newly released). */
export function useReleaseBadges(): UseQueryResult<ReleaseBadges> {
  const { teamId, enabled } = useReleaseBrandGate();
  return useQuery({
    queryKey: teamQueryKeys.approval.board.badges(teamId),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => releaseSectionsService.getBadges(requireTeamId(teamId)),
    enabled,
    staleTime: 30_000,
    refetchInterval: RELEASE_BADGES_POLL_MS,
    refetchOnWindowFocus: true,
  });
}

export interface ReleaseNavCounts {
  /** Reviewers: waiting for your release */
  awaiting: number;
  /** Operators: newly released since the last calendar visit */
  newlyReleased: number;
}

export function useReleaseNavCounts(): ReleaseNavCounts {
  const { data } = useReleaseBadges();
  return useMemo(() => {
    if (!data?.workflowActive) return { awaiting: 0, newlyReleased: 0 };
    return {
      awaiting: Math.max(0, data.awaitingForYou ?? 0),
      newlyReleased: Math.max(0, data.calendar?.newlyReleased ?? 0),
    };
  }, [data]);
}

export type NavBadgeKind = 'awaiting' | 'newlyReleased';

export interface NavReleaseBadge {
  count: number;
  kind: NavBadgeKind;
}

/**
 * Release badges per nav item id, shared by the rail (desktop) and the bottom bar (mobile):
 * approvals = waiting for your release, calendar = newly released, agency-portfolio = waiting
 * for your release across the portfolio (staff reviewers). Items without a count are absent.
 */
export function useNavReleaseBadges(): Record<string, NavReleaseBadge> {
  const { user } = useAuth();
  const nav = useReleaseNavCounts();
  const outsideGodMode = user?.isAgencyStaff === true && !user.godModeSession;
  const { data: inbox } = useReleaseInboxCounts(outsideGodMode && user?.agencyIsReviewer === true);

  return useMemo(() => {
    const badges: Record<string, NavReleaseBadge> = {};
    if (nav.awaiting > 0) badges.approvals = { count: nav.awaiting, kind: 'awaiting' };
    if (nav.newlyReleased > 0) badges.calendar = { count: nav.newlyReleased, kind: 'newlyReleased' };
    const portfolio = Math.max(0, inbox?.awaiting ?? 0);
    if (outsideGodMode && portfolio > 0) {
      badges['agency-portfolio'] = { count: portfolio, kind: 'awaiting' };
    }
    return badges;
  }, [nav, inbox, outsideGodMode]);
}

// ============================================================================
// Surface stamps
// ============================================================================

interface MarkSeenVariables {
  teamId: number;
  surface: ReleaseSurface;
  asOf?: string;
}

/**
 * Stamp a surface as seen. Does NOT invalidate sections. Visiting the calendar clears the
 * calendar nav badge right away by patching the badge cache.
 */
export function useMarkSurfaceSeen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, surface, asOf }: MarkSeenVariables) =>
      releaseSectionsService.markSeen(teamId, surface, asOf),
    onSuccess: (_data, { teamId, surface }) => {
      if (surface !== 'calendar') return;
      queryClient.setQueryData<ReleaseBadges>(
        teamQueryKeys.approval.board.badges(teamId),
        (current) =>
          current?.calendar
            ? { ...current, calendar: { ...current.calendar, newlyReleased: 0 } }
            : current
      );
    },
  });
}

// ============================================================================
// Dashboard
// ============================================================================

export interface DashboardReleaseSections {
  data: BrandReleaseSections | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /** True when the item was new at the first load of this visit, or is new since the stamp */
  isNew: (item: SectionItem) => boolean;
}

/**
 * Release rows of the dashboard. After the first successful load the dashboard stamp is moved
 * to the server's `asOf`; the "new" cues of that first load are kept for the whole visit, so a
 * later refetch (focus, SSE) cannot wipe them.
 */
export function useDashboardReleaseSections(
  limit: number = DASHBOARD_SECTION_LIMIT
): DashboardReleaseSections {
  const { teamId, enabled } = useReleaseBrandGate();
  const { mutate: markSeenMutate } = useMarkSurfaceSeen();

  const query = useQuery({
    queryKey: teamQueryKeys.dashboard.releaseSections(teamId, limit),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => releaseSectionsService.getBrandSections(requireTeamId(teamId), limit),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    // A revisit must not replay cached cues or re-stamp with an old asOf: drop the cache on leave
    // and always fetch on mount.
    gcTime: 0,
    refetchOnMount: 'always',
  });
  const isFreshFetch = query.isFetchedAfterMount;

  const [frozenNew, setFrozenNew] = useState<{ teamId: number; ids: ReadonlySet<string> } | null>(null);
  const stampedTeamRef = useRef<number | null>(null);
  const data = query.data;

  useEffect(() => {
    if (!isFreshFetch || teamId === null || !data?.workflowActive || !data.sections) return;
    if (stampedTeamRef.current === teamId) return;
    stampedTeamRef.current = teamId;

    const ids = new Set<string>();
    for (const section of Object.values(data.sections)) {
      for (const item of section?.items ?? []) {
        if (item.isNew) ids.add(item.id);
      }
    }
    setFrozenNew({ teamId, ids });
    markSeenMutate({ teamId, surface: 'dashboard', asOf: data.asOf });
  }, [data, isFreshFetch, teamId, markSeenMutate]);

  const frozenIds = frozenNew && frozenNew.teamId === teamId ? frozenNew.ids : null;
  const isNew = useCallback(
    (item: SectionItem): boolean => item.isNew || (frozenIds?.has(item.id) ?? false),
    [frozenIds]
  );

  const { refetch } = query;
  return {
    data,
    isLoading: enabled && query.isPending,
    isError: query.isError && !query.data,
    refetch: () => void refetch(),
    isNew,
  };
}

// ============================================================================
// Calendar nudge
// ============================================================================

export interface NewlyReleasedState {
  /** Number of newly released items (server total, may exceed the 100 listed) */
  count: number;
  /** `<postId>:base` / `<postId>:<variationId>` of the listed items */
  itemKeys: ReadonlySet<string>;
  /** Distinct post ids of the listed items */
  postIds: number[];
  /** Exact versions and their release times, newest first. */
  items: NewlyReleased['items'];
  isLoading: boolean;
  /** Stamp only when every version in the fetched window was displayed, mapped, or confirmed unavailable. */
  acknowledge: (accountedItemKeys: ReadonlySet<string>) => void;
}

const EMPTY_KEYS: ReadonlySet<string> = new Set<string>();

/**
 * Items released since the viewer's last calendar visit (operators only).
 * The first response's `since` is frozen for the rest of the visit: later refetches (invalidation,
 * focus) ask for the same window, so scheduling one item shrinks the set but a refetch can never
 * wipe it. The Calendar seen stamp advances after shelf dismissal, or when a successful
 * post-list fetch proves that every version in the window has become unavailable.
 */
export function useNewlyReleased(enabled: boolean): NewlyReleasedState {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();
  const { mutate: markSeenMutate } = useMarkSurfaceSeen();

  const [frozen, setFrozen] = useState<{ teamId: number; since: string; asOf: string } | null>(null);
  const since = frozen && frozen.teamId === teamId ? frozen.since : null;
  const frozenTeamRef = useRef<number | null>(null);
  const acknowledgedRef = useRef<string | null>(null);

  const query = useQuery({
    queryKey: teamQueryKeys.approval.board.newlyReleased(teamId, since),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => releaseSectionsService.getNewlyReleased(requireTeamId(teamId), since),
    enabled: enabled && teamId !== null,
    staleTime: 30_000,
    // Initial (unfrozen) window: never reuse a cached response from an earlier visit.
    gcTime: since === null ? 0 : undefined,
    refetchOnMount: since === null ? 'always' : true,
  });
  const data: NewlyReleased | undefined = query.data;
  const isFreshFetch = query.isFetchedAfterMount;

  useEffect(() => {
    if (!data || !isFreshFetch || teamId === null || frozenTeamRef.current === teamId) return;
    frozenTeamRef.current = teamId;
    // Freeze the window: seed the cache for the frozen key so the key change costs no request.
    queryClient.setQueryData(teamQueryKeys.approval.board.newlyReleased(teamId, data.since), data);
    setFrozen({ teamId, since: data.since, asOf: data.asOf });
  }, [data, isFreshFetch, teamId, queryClient]);

  const acknowledge = useCallback((accountedItemKeys: ReadonlySet<string>) => {
    // A single timestamp cannot acknowledge individual versions. If the server
    // truncated the result at 100, or any version is unaccounted for, leave the
    // stamp alone so unseen releases can appear on the next visit.
    if (teamId === null || !frozen || frozen.teamId !== teamId || !data || query.isError ||
        data.total !== data.items.length ||
        !data.items.every((item) => accountedItemKeys.has(releaseItemKey(item.postId, item.variationId)))) return;
    const key = `${teamId}:${frozen.asOf}`;
    if (acknowledgedRef.current === key) return;
    acknowledgedRef.current = key;
    markSeenMutate(
      { teamId, surface: 'calendar', asOf: frozen.asOf },
      { onError: () => { if (acknowledgedRef.current === key) acknowledgedRef.current = null; } }
    );
  }, [teamId, frozen, data, query.isError, markSeenMutate]);

  return useMemo(() => {
    if (!data || data.items.length === 0) {
      return { count: data?.total ?? 0, itemKeys: EMPTY_KEYS, postIds: [], items: [], isLoading: query.isPending && enabled, acknowledge };
    }
    return {
      count: data.total,
      itemKeys: new Set(data.items.map((item) => releaseItemKey(item.postId, item.variationId))),
      postIds: Array.from(new Set(data.items.map((item) => item.postId))),
      items: data.items,
      isLoading: false,
      acknowledge,
    };
  }, [data, query.isPending, enabled, acknowledge]);
}

// ============================================================================
// Brand Portfolio inbox
// ============================================================================

export interface ReleaseInboxState {
  items: SectionItem[];
  total: number | null;
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
}

/** Cursor paged section across the portfolio. `teamFilter` narrows it to one brand. */
export function useReleaseInbox(
  section: ReleaseInboxSectionKey,
  teamFilter: number | null,
  enabled: boolean
): ReleaseInboxState {
  const query = useInfiniteQuery({
    queryKey: releaseInboxKeys.section(section, teamFilter),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: ({ pageParam }) =>
      releaseSectionsService.getInboxPage({
        section,
        teamId: teamFilter,
        cursor: pageParam,
        limit: RELEASE_INBOX_PAGE_SIZE,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const pages = query.data?.pages;
  const items = useMemo<SectionItem[]>(() => {
    const seen = new Set<string>();
    const merged: SectionItem[] = [];
    for (const page of pages ?? []) {
      for (const item of page.items) {
        const key = `${item.teamId}:${item.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(item);
      }
    }
    return merged;
  }, [pages]);

  const { fetchNextPage, refetch } = query;
  return {
    items,
    total: pages?.[0]?.total ?? null,
    isLoading: enabled && query.isPending,
    isError: query.isError && !query.data,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => void fetchNextPage(),
    refetch: () => void refetch(),
  };
}

/** Portfolio-wide counts (inbox tabs, portfolio badge). */
export function useReleaseInboxCounts(enabled: boolean): UseQueryResult<ReleaseInboxCounts> {
  return useQuery({
    queryKey: releaseInboxKeys.counts(),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => releaseSectionsService.getInboxCounts(),
    enabled,
    staleTime: 30_000,
    refetchInterval: INBOX_COUNTS_POLL_MS,
    refetchOnWindowFocus: true,
  });
}
